/** El estado de la conversación, dirigido por los eventos del stream.
 *
 * Un `useReducer` y no varios `useState`: los eventos llegan intercalados (progreso de una
 * herramienta mientras otra devuelve su tarjeta) y con estados separados el orden de las
 * actualizaciones decidía lo que se veía.
 */
import { useCallback, useReducer, useRef } from "react";
import { api } from "../lib/api";
import type { Evento, Idioma, Tarjeta, Turno, TurnoGuardado } from "../lib/tipos";

interface Estado {
  turnos: Turno[];
  sesionId: string | null;
  error: string | null;
}

type Accion =
  | { t: "sesion"; id: string | null }
  | { t: "preguntar"; pregunta: string; idioma: Idioma }
  | { t: "evento"; evento: Evento }
  | { t: "error"; mensaje: string }
  | { t: "cargar"; sesionId: string; turnos: TurnoGuardado[] }
  | { t: "limpiar" };

const VACIO: Estado = { turnos: [], sesionId: null, error: null };

function nuevoTurno(pregunta: string, idioma: Idioma): Turno {
  return { id: `t${Date.now()}`, pregunta, idioma, respuesta_md: "", tarjetas: [], progreso: [],
           notas: [], avisos: [], meta: null, enVuelo: true, revisando: null };
}

function reducir(estado: Estado, a: Accion): Estado {
  switch (a.t) {
    case "sesion":
      return { ...estado, sesionId: a.id };
    case "limpiar":
      return { ...VACIO };
    case "cargar":
      return {
        sesionId: a.sesionId, error: null,
        turnos: a.turnos.map((g) => ({
          id: g.id, pregunta: g.pregunta, idioma: g.idioma, respuesta_md: g.respuesta_md,
          tarjetas: g.tarjetas, progreso: [], notas: [], avisos: [], meta: g.meta,
          turno_id: g.id, enVuelo: false, revisando: null,
        })),
      };
    case "preguntar":
      return { ...estado, error: null, turnos: [...estado.turnos, nuevoTurno(a.pregunta, a.idioma)] };
    case "error":
      return { ...estado, error: a.mensaje, turnos: estado.turnos.map((t, i) => (i === estado.turnos.length - 1 ? { ...t, enVuelo: false } : t)) };
    case "evento":
      return { ...estado, ...aplicar(estado, a.evento) };
  }
}

function aplicar(estado: Estado, e: Evento): Partial<Estado> {
  const i = estado.turnos.length - 1;
  if (i < 0) return {};
  const turno = estado.turnos[i]!;
  const cambiar = (parche: Partial<Turno>): Partial<Estado> => ({
    turnos: estado.turnos.map((t, j) => (j === i ? { ...t, ...parche } : t)),
  });

  switch (e.tipo) {
    case "tool_pensando":
      return cambiar({ progreso: [...turno.progreso, { id: e.id, tool: e.tool, etiqueta: "", estado: "pensando" }] });
    case "tool_start": {
      const existe = turno.progreso.some((p) => p.id === e.id);
      const paso = { id: e.id ?? e.tool, tool: e.tool, etiqueta: e.etiqueta, estado: "corriendo" as const };
      return cambiar({ progreso: existe ? turno.progreso.map((p) => (p.id === e.id ? paso : p)) : [...turno.progreso, paso] });
    }
    case "tool_result": {
      const tarjeta = e.tarjeta as Tarjeta;
      return cambiar({
        tarjetas: [...turno.tarjetas, tarjeta],
        progreso: turno.progreso.map((p) => (p.tool === tarjeta.tool && p.estado !== "listo" ? { ...p, estado: "listo", etiqueta: p.etiqueta } : p)),
      });
    }
    case "tool_error":
      return cambiar({ progreso: turno.progreso.map((p) => (p.id === e.id ? { ...p, estado: "error", mensaje: e.mensaje } : p)) });
    case "nota":
      return cambiar({ notas: [...turno.notas, e.texto] });
    case "aviso":
      return cambiar({ avisos: [...turno.avisos, e.mensaje] });
    case "texto":
      return cambiar({ respuesta_md: turno.respuesta_md + e.delta, revisando: null });
    case "texto_reset":
      // El texto ya se transmitió y el gate lo rechazó: se descarta el borrador y se avisa.
      return cambiar({ respuesta_md: "", revisando: e.motivo });
    case "fin":
      return {
        sesionId: e.sesion_id,
        turnos: estado.turnos.map((t, j) =>
          j === i ? { ...t, respuesta_md: e.respuesta_md, meta: e.meta, turno_id: e.turno_id, enVuelo: false, revisando: null } : t),
      };
    case "error":
      return { error: e.mensaje, turnos: estado.turnos.map((t, j) => (j === i ? { ...t, enVuelo: false } : t)) };
  }
}

export function useChat() {
  const [estado, despachar] = useReducer(reducir, VACIO);
  const abortar = useRef<AbortController | null>(null);

  const preguntar = useCallback(async (mensaje: string, idioma: Idioma | null) => {
    despachar({ t: "preguntar", pregunta: mensaje, idioma: idioma ?? "en" });
    abortar.current?.abort();
    const control = new AbortController();
    abortar.current = control;
    try {
      for await (const evento of api.chat(mensaje, estado.sesionId, idioma, control.signal)) {
        despachar({ t: "evento", evento });
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") despachar({ t: "error", mensaje: String((err as Error).message ?? err) });
    } finally {
      abortar.current = null;
    }
  }, [estado.sesionId]);

  const detener = useCallback(() => abortar.current?.abort(), []);
  const abrir = useCallback(async (sesionId: string) => {
    const d = await api.sesion(sesionId);
    despachar({ t: "cargar", sesionId, turnos: d.turnos });
  }, []);
  const nueva = useCallback(() => despachar({ t: "limpiar" }), []);

  return { ...estado, preguntar, detener, abrir, nueva, reducir };
}

export { reducir, VACIO };
export type { Estado, Accion };
